import React from 'react';
import { Box, Chip, Dialog, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { formatBerlin } from './shared';

/**
 * Render contract from `/api/adserver/external-sams/preview/:id`.
 * Returned by both the External SAM tab and the Asset Selection history tab,
 * so any future tweak (iframe sandboxing, copy-button, etc.) lives here once.
 */
export interface SamHtmlPreviewData {
  id: number;
  /** External SAM has a client code (dnv/be/bg/sw/tt/kme). FT SAM doesn't — `null` there. */
  client_code: string | null;
  send_date: string;
  status: string;
  mc_campaign_id: string | null;
  mc_campaign_title: string | null;
  material_type: string | null;
  asset_isin: string | null;
  asset_name: string | null;
  sam_subject: string | null;
  /** FT SAM Main vs Dup. `null` / 0 = Main, 1 = Dup. Omitted for External rows. */
  dup_send?: number | null;
  html: string | null;
  /** 'cache' = mc_campaign_html_history (External), 'mailchimp' = live MC API
   *  (External fallback), 'ft_db' = mc_automated_sams.inner_html (FT SAM),
   *  'ft_render' = FT AdServer material renderer (never-sent pool SAMs,
   *  Scoring tab), 'none' = no html available. */
  html_source: 'cache' | 'mailchimp' | 'ft_db' | 'ft_render' | 'none';
  source_error: string | null;
}

interface Props {
  open: boolean;
  preview: SamHtmlPreviewData | null;
  onClose: () => void;
}

const SamHtmlPreviewDialog: React.FC<Props> = ({ open, preview, onClose }) => {
  const title = preview?.mc_campaign_title || preview?.sam_subject || 'Vorschau';
  const isAa = preview?.material_type === 'asset_analysis';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {title}
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1, mb: 0.5 }}>
          {preview?.client_code && (
            <Chip size="small" color="primary" label={preview.client_code.toUpperCase()} />
          )}
          {preview?.send_date && (
            <Typography variant="body2" color="text.secondary">
              {formatBerlin(preview.send_date)}
            </Typography>
          )}
          {isAa && preview?.asset_isin && (
            <Chip
              size="small"
              color="info"
              label={
                <>
                  <code>{preview.asset_isin}</code>
                  {preview.asset_name ? ` — ${preview.asset_name}` : ''}
                </>
              }
            />
          )}
          {preview?.dup_send === 1 && <Chip size="small" label="Dup" />}
          {preview?.html_source === 'mailchimp' && (
            <Chip size="small" variant="outlined" label="Live aus Mailchimp" />
          )}
          {preview?.html_source === 'cache' && (
            <Chip size="small" variant="outlined" label="Cache" />
          )}
          {preview?.html_source === 'ft_db' && (
            <Chip size="small" variant="outlined" label="FT Render" />
          )}
          {preview?.html_source === 'ft_render' && (
            <Chip size="small" variant="outlined" label="Material-Render (nie versendet)" />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {preview?.html ? (
          <iframe
            sandbox=""
            srcDoc={preview.html}
            title="SAM Vorschau"
            style={{ width: '100%', height: '70vh', border: 0, backgroundColor: '#fff' }}
          />
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {/* Message order:
                  1. MC linked but no HTML anywhere → cache+MC both empty
                  2. No MC linked AND row is External (client_code non-null) →
                     pre-MC-creation abort. The html_source='none' from the
                     External backend would otherwise misroute to the FT msg.
                  3. No MC linked AND row is FT (client_code null) → inner_html
                     was never persisted (pre-Pipeline-A legacy FTWP row). */}
              {preview?.mc_campaign_id
                ? 'Kein HTML verfügbar (weder im Cache noch live von Mailchimp).'
                : preview?.client_code != null
                  ? 'Noch keine Mailchimp-Campaign verknüpft (mc_campaign_id NULL — Versand wurde vor der MC-Erstellung abgebrochen oder ist noch nicht eingeplant).'
                  : 'Kein inner_html gespeichert (vermutlich pre-Pipeline-A FTWP-Row).'}
            </Typography>
            {preview?.source_error && (
              <Typography variant="caption" color="error.main">
                Quelle: {preview.source_error}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SamHtmlPreviewDialog;
